import { useState, useRef, useEffect } from "react";
import {
  AlertTriangle, CheckCircle2, XCircle, Minus,
  ChevronDown, ChevronUp, Ban, Cpu, FileText,
  Clock, Tag,
} from "lucide-react";
import type { ValidateFileResult, ValidateRuleResult } from "../../api/frameworksApi";

export function isValidationPassed(result: ValidateFileResult): boolean {
  return result.summary.fail === 0;
}

const STATUS_ORDER: Record<string, number> = { FAIL: 0, WARN: 1, SKIPPED: 2, PASS: 3 };

/** Exported so the processing console reuses the same category labels. */
export const CATEGORY_META: Record<string, { label: string; description: string; isLlm?: boolean }> = {
  metadata:   { label: "Metadata",   description: "Filename, dates, classification and doc-type fields" },
  structural: { label: "Structural", description: "Headings, sections, footer and layout checks" },
  semantic:   { label: "Semantic",   description: "AI-reviewed intent, risk and control alignment", isLlm: true },
};

/* ── Status helpers ──────────────────────────────────────────────────────── */

/**
 * Exported so the processing console's validation feed renders findings with
 * exactly the same status colours as this panel.
 */
export const STATUS_CONFIG = {
  PASS:    { icon: CheckCircle2, iconCls: "text-emerald-500", rowBg: "", pillCls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20", label: "Passed" },
  FAIL:    { icon: XCircle,      iconCls: "text-red-500",     rowBg: "bg-red-50/40 dark:bg-red-500/[0.04]", pillCls: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20", label: "Failed" },
  WARN:    { icon: AlertTriangle,iconCls: "text-amber-500",   rowBg: "bg-amber-50/40 dark:bg-amber-500/[0.04]", pillCls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20", label: "Warning" },
  SKIPPED: { icon: Minus,        iconCls: "text-slate-400",   rowBg: "", pillCls: "bg-slate-100 text-slate-500 border-slate-200 dark:bg-white/[0.04] dark:text-white/60 dark:border-white/[0.08]", label: "Skipped" },
} as const;

const SEVERITY_BADGE_CLS = "flex-shrink-0 text-[9px] font-black py-0.5 rounded border w-[5.5rem] text-center";

const SEVERITY_CONFIG: Record<string, { cls: string; label: string }> = {
  ERROR:     { label: "ERROR",     cls: "bg-orange-100 text-orange-600 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20" },
  WARN:      { label: "WARN",      cls: "bg-amber-100 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20" },
  MANDATORY: { label: "MANDATORY", cls: "bg-indigo-100 text-indigo-600 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20" },
  STANDARD:  { label: "STANDARD",  cls: "bg-yellow-100 text-yellow-600 border-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-400 dark:border-yellow-500/20" },
};

/* ── Segmented compliance bar with hover tooltip ─────────────────────────── */
interface SegmentBarProps {
  pass: number;
  fail: number;
  warn: number;
  skipped?: number;
  total: number;
  height?: string;
}

interface TipState {
  label: string;
  count: number;
  pct: number;
  color: string;
  x: number;
  y: number;
}

function SegmentBar({ pass, fail, warn, total, height = "h-2" }: SegmentBarProps) {
  const [tip, setTip] = useState<TipState | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  // Clamp tooltip horizontally so it never escapes the viewport
  useEffect(() => {
    if (!tip || !tipRef.current) return;
    const el = tipRef.current;
    el.style.transform = "translateX(-50%)";
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    if (rect.right > vw - 8) {
      el.style.transform = `translateX(calc(-50% + ${vw - 8 - rect.right}px))`;
    } else if (rect.left < 8) {
      el.style.transform = `translateX(calc(-50% + ${8 - rect.left}px))`;
    }
  }, [tip]);

  if (total === 0) return null;

  const visibleTotal = pass + fail + warn;
  const base = visibleTotal > 0 ? visibleTotal : total;
  const segments = [
    { key: "pass", count: pass, pct: (pass / base) * 100, color: "#10b981", bg: "bg-emerald-500", label: "Passed" },
    { key: "warn", count: warn, pct: (warn / base) * 100, color: "#f59e0b", bg: "bg-amber-400",   label: "Warning" },
    { key: "fail", count: fail, pct: (fail / base) * 100, color: "#ef4444", bg: "bg-red-500",     label: "Failed" },
  ].filter((s) => s.count > 0);

  return (
    <div className="relative w-full">
      {/* Bar track */}
      <div className={`${height} w-full rounded-full overflow-hidden bg-[#e2e8f0] dark:bg-white/[0.07] flex gap-px`}>
        {segments.map((s) => (
          <div
            key={s.key}
            className={`h-full ${s.bg} transition-all duration-300 cursor-pointer first:rounded-l-full last:rounded-r-full hover:brightness-110`}
            style={{ width: `${s.pct}%` }}
            onMouseEnter={(e) => {
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setTip({ label: s.label, count: s.count, pct: s.pct, color: s.color, x: r.left + r.width / 2, y: r.top });
            }}
            onMouseLeave={() => setTip(null)}
          />
        ))}
      </div>

      {/* Tooltip — fixed to viewport so it's never clipped */}
      {tip && (
        <div
          ref={tipRef}
          className="fixed z-[9999] pointer-events-none"
          style={{ left: tip.x, top: tip.y - 48, transform: "translateX(-50%)" }}
        >
          <div
            className="flex flex-col items-center"
            style={{ filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.45))" }}
          >
            <div className="bg-[#0c0f1d] border border-white/[0.1] rounded-lg px-3 py-1.5 whitespace-nowrap">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full flex-shrink-0" style={{ backgroundColor: tip.color }} />
                <span className="text-[11px] font-semibold text-white/90">{tip.label}</span>
                <span className="text-[12px] font-black text-white">{tip.count}</span>
                <span className="text-[10px] text-white/60">/ {total}</span>
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: tip.color + "28", color: tip.color }}
                >
                  {tip.pct.toFixed(0)}%
                </span>
              </div>
            </div>
            {/* Arrow */}
            <div className="size-2.5 rotate-45 rounded-[2px] -translate-y-[3px]" style={{ backgroundColor: "#0c0f1d", border: "1px solid rgba(255,255,255,0.1)" }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Rule row ────────────────────────────────────────────────────────────── */
function RuleRow({ rule, index }: { rule: ValidateRuleResult; index: number }) {
  const [open, setOpen] = useState(false);
  const expandable = rule.status !== "SKIPPED";
  const cfg = STATUS_CONFIG[rule.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.SKIPPED;
  const Icon = cfg.icon;
  const sevCfg = SEVERITY_CONFIG[rule.severity] ?? SEVERITY_CONFIG["WARN"];

  return (
    <div className={`border-b border-[#f1f5f9] dark:border-white/[0.04] last:border-0 ${cfg.rowBg}`}>
      {/* Row header */}
      <div
        className={`flex items-center gap-3 px-4 py-3 ${expandable ? "cursor-pointer select-none" : ""}`}
        onClick={() => expandable && setOpen((v) => !v)}
      >
        {/* Index */}
        <span className="flex-shrink-0 w-5 text-center text-[10px] font-bold text-[#94a3b8] dark:text-white/50">
          {index + 1}
        </span>

        {/* Status icon */}
        <Icon className={`size-4 flex-shrink-0 ${cfg.iconCls}`} strokeWidth={2.5} />

        {/* Rule name */}
        <span className={`text-[13px] font-semibold flex-1 min-w-0 leading-snug ${
          rule.status === "PASS"
            ? "text-[#475569] dark:text-white/80"
            : "text-[#0f172a] dark:text-white"
        }`}>
          {rule.name}
        </span>

        {/* Rule ID */}
        <span className="flex-shrink-0 font-mono text-[11px] font-black text-[#475569] dark:text-white/80 hidden sm:inline tracking-wide">
          {rule.ruleId}
        </span>

        {/* Severity badge — always shown */}
        <span className={`${SEVERITY_BADGE_CLS} ${sevCfg.cls}`}>
          {sevCfg.label}
        </span>

        {/* Expand chevron */}
        {expandable ? (
          open
            ? <ChevronUp className="size-3.5 flex-shrink-0 text-[#64748b] dark:text-white/60" />
            : <ChevronDown className="size-3.5 flex-shrink-0 text-[#64748b] dark:text-white/60" />
        ) : (
          <div className="size-3.5 flex-shrink-0" />
        )}
      </div>

      {/* Expanded detail */}
      {open && (
        <div
          className="mx-4 mb-3 ml-12 rounded-xl overflow-hidden border border-[#e2e8f0] dark:border-white/[0.08]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Evidence */}
          {rule.evidence && (
            <div className="px-3.5 py-3 bg-slate-50 dark:bg-[#161b2e]">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="size-1.5 rounded-full bg-indigo-400 dark:bg-indigo-500 flex-shrink-0" />
                <p className="text-[10px] font-bold tracking-widest text-indigo-500 dark:text-indigo-400">
                  Evidence
                </p>
              </div>
              <p className="text-[12px] text-[#475569] dark:text-white/80 leading-relaxed font-mono bg-white dark:bg-white/[0.04] rounded-lg px-3 py-2 border border-[#e2e8f0] dark:border-white/[0.06]">
                {rule.evidence.length > 240 ? rule.evidence.slice(0, 240) + "…" : rule.evidence}
              </p>
            </div>
          )}

          {/* Remediation */}
          {rule.remediation && (
            <div className={`px-3.5 py-3 border-t border-[#e2e8f0] dark:border-white/[0.06] ${
              rule.status === "FAIL"
                ? "bg-rose-50/80 dark:bg-[#1e1020]"
                : rule.status === "WARN"
                ? "bg-amber-50/60 dark:bg-[#1c1810]"
                : "bg-emerald-50/60 dark:bg-[#0d1a14]"
            }`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`size-1.5 rounded-full flex-shrink-0 ${
                  rule.status === "FAIL" ? "bg-rose-500"
                  : rule.status === "WARN" ? "bg-amber-500"
                  : "bg-emerald-500"
                }`} />
                <span className={`text-[10px] font-bold tracking-widest ${
                  rule.status === "FAIL"
                    ? "text-rose-600 dark:text-rose-400"
                    : rule.status === "WARN"
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-emerald-600 dark:text-emerald-400"
                }`}>
                  {rule.status === "PASS" ? "Guidance" : "How to fix"}
                </span>
              </div>
              <p className="text-[12px] leading-relaxed text-[#374151] dark:text-white/85">
                {rule.remediation}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Category section ────────────────────────────────────────────────────── */
function CategorySection({
  category,
  rules,
  summary,
}: {
  category: string;
  rules: ValidateRuleResult[];
  summary: { pass: number; fail: number; warn: number; skipped: number; total: number };
}) {
  const [collapsed, setCollapsed] = useState(false);
  const meta = CATEGORY_META[category] ?? {
    label: category.charAt(0).toUpperCase() + category.slice(1),
    description: "",
  };
  const sorted = [...rules].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
  const allPassed = summary.fail === 0 && summary.warn === 0;

  return (
    <div className="rounded-xl overflow-hidden border border-[#e2e8f0] dark:border-white/[0.07]">
      {/* Category header — clickable to collapse */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white dark:bg-[#0f1120] hover:bg-[#f8fafc] dark:hover:bg-white/[0.03] transition-colors border-b border-[#e2e8f0] dark:border-white/[0.07]"
      >
        {/* Status dot */}
        <span className={`size-2 rounded-full flex-shrink-0 ${
          allPassed ? "bg-emerald-500" : summary.fail > 0 ? "bg-red-500" : "bg-amber-500"
        }`} />

        {/* Label + description */}
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-bold text-[#0f172a] dark:text-white tracking-wide">
              {meta.label}
            </span>
            {meta.isLlm && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-600 border border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20">
                <Cpu className="size-2.5" /> AI
              </span>
            )}
          </div>
          {meta.description && (
            <p className="text-[12px] text-[#475569] dark:text-white/70 leading-none mt-0.5 truncate">
              {meta.description}
            </p>
          )}
        </div>


        {/* Counts + progress bar + collapse icon */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Pass/Warn/Fail counts — only show non-zero */}
          <div className="flex items-center gap-1">
            {summary.pass > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                <span>✓</span>{summary.pass}
              </span>
            )}
            {summary.warn > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                <span>!</span>{summary.warn}
              </span>
            )}
            {summary.fail > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">
                <span>✕</span>{summary.fail}
              </span>
            )}
          </div>
          <div className="w-24">
            <SegmentBar pass={summary.pass} fail={summary.fail} warn={summary.warn} skipped={0} total={summary.total} height="h-2" />
          </div>
          {collapsed
            ? <ChevronDown className="size-3.5 text-[#64748b] dark:text-white/60 flex-shrink-0" />
            : <ChevronUp className="size-3.5 text-[#64748b] dark:text-white/60 flex-shrink-0" />
          }
        </div>
      </button>

      {/* Rule list */}
      {!collapsed && (
        <div className="bg-white dark:bg-[#0a0d1a]">
          {sorted.map((rule, i) => (
            <RuleRow key={rule.ruleId} rule={rule} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main panel ──────────────────────────────────────────────────────────── */
interface Props {
  result: ValidateFileResult;
}

export function ValidationResultPanel({ result }: Props) {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const blocked = !isValidationPassed(result);
  const summary = result.summary ?? { pass: 0, fail: 0, warn: 0, skipped: 0, total: 0 };
  const summaryByCategory: Record<string, typeof summary> = result.summaryByCategory ?? {};
  const resultsByCategory: Record<string, ValidateRuleResult[]> = result.resultsByCategory ?? {};


  const allCategories = Object.keys(resultsByCategory);
  const visibleCategories = activeCategory === "all" ? allCategories : [activeCategory];

  const tabs = [
    { key: "all", label: "All", sum: summary },
    ...allCategories.map((cat) => ({
      key: cat,
      label: CATEGORY_META[cat]?.label ?? cat.charAt(0).toUpperCase() + cat.slice(1),
      sum: summaryByCategory[cat] ?? { pass: 0, fail: 0, warn: 0, skipped: 0, total: 0 },
      isLlm: CATEGORY_META[cat]?.isLlm,
    })),
  ];

  return (
    <div className="space-y-3">

      {/* ── Overall summary card ── */}
      <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-[#0f1120] overflow-hidden">
        {/* Top band */}
        <div className={`h-1 w-full ${summary.fail > 0 ? "bg-red-500" : summary.warn > 0 ? "bg-amber-400" : "bg-emerald-500"}`} />

        <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
          {/* File info */}
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div className="size-9 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.07] flex items-center justify-center flex-shrink-0">
              <FileText className="size-4 text-[#64748b] dark:text-white/70" />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-[#0f172a] dark:text-white truncate leading-tight">
                {result.filename}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="inline-flex items-center gap-1 text-[12px] text-[#475569] dark:text-white/80">
                  <Tag className="size-3" />{result.docType}
                </span>
                <span className="text-[#e2e8f0] dark:text-white/30">·</span>
                <span className="inline-flex items-center gap-1 text-[12px] text-[#475569] dark:text-white/80">
                  <Clock className="size-3" />{result.durationMs} ms
                </span>
              </div>
            </div>
          </div>

          {/* Score counters */}
          <div className="flex items-stretch gap-0 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden flex-shrink-0">
            {[
              { label: "Pass",  count: summary.pass,  bg: "bg-emerald-50 dark:bg-emerald-500/[0.08]", text: "text-emerald-700 dark:text-emerald-400", sub: "text-emerald-500/70 dark:text-emerald-400/80" },
              { label: "Warn",  count: summary.warn,  bg: "bg-amber-50 dark:bg-amber-500/[0.08]",     text: "text-amber-700 dark:text-amber-400",     sub: "text-amber-500/70 dark:text-amber-400/80" },
              { label: "Fail",  count: summary.fail,  bg: "bg-red-50 dark:bg-red-500/[0.08]",         text: "text-red-700 dark:text-red-400",         sub: "text-red-500/70 dark:text-red-400/80" },
            ].map((s, i, arr) => (
              <div key={s.label} className={`flex flex-col items-center px-3 py-1.5 ${s.bg} ${i < arr.length - 1 ? "border-r border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}>
                <span className={`text-base font-black leading-none ${s.text}`}>{s.count}</span>
                <span className={`text-[8px] font-bold tracking-widest mt-0.5 ${s.sub}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Overall progress bar */}
        <div className="px-4 pb-3">
          <SegmentBar pass={summary.pass} fail={summary.fail} warn={summary.warn} skipped={0} total={summary.total} height="h-2.5" />
        </div>
      </div>

      {/* ── Blocked banner ── */}
      {blocked && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
          <div className="size-7 rounded-lg bg-red-100 dark:bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <Ban className="size-3.5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-[13px] font-bold text-red-700 dark:text-red-300">Cannot upload — validation errors found</p>
            <p className="text-[12px] text-red-600 dark:text-red-200/90">Please fix the errors below or choose a updated file to proceed.</p>
          </div>
        </div>
      )}

      {/* ── Category filter tabs ── */}
      <div className="flex items-center gap-1.5 p-1 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06]">
        {tabs.map((tab) => {
          const isActive = activeCategory === tab.key;
          const hasFail = tab.sum.fail > 0;
          const hasWarn = tab.sum.warn > 0;
          const dotColor = hasFail ? "bg-red-500" : hasWarn ? "bg-amber-400" : "bg-emerald-500";
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveCategory(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${
                isActive
                  ? "bg-white dark:bg-[#0f1120] text-[#0f172a] dark:text-white shadow-sm border border-[#e2e8f0] dark:border-white/[0.08]"
                  : "text-[#64748b] dark:text-white/80 hover:text-[#0f172a] dark:hover:text-white"
              }`}
            >
              <span className={`size-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
              {tab.label}
              {"isLlm" in tab && tab.isLlm && (
                <span className="text-[8px] font-black px-1 py-0.5 rounded bg-violet-100 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400 leading-none">AI</span>
              )}
              <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ml-0.5 ${
                isActive
                  ? hasFail ? "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400"
                    : "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400"
                  : "bg-white/60 text-[#64748b] dark:bg-white/[0.06] dark:text-white/80"
              }`}>
                {tab.sum.total}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Category sections ── */}
      {visibleCategories.map((cat) => (
        <CategorySection
          key={cat}
          category={cat}
          rules={resultsByCategory[cat] ?? []}
          summary={summaryByCategory[cat] ?? { pass: 0, fail: 0, warn: 0, skipped: 0, total: 0 }}
        />
      ))}
    </div>
  );
}
